import dayjs from 'dayjs'
import { useEffect } from 'react'

import type { usePublicationsOfCommunityQuery } from '../../services/usePublicationsOfCommunityQuery'

/**
 * Finds the publication dated today among a community publications.
 * Publications are ordered from the oldest, so today's one may not be on the first loaded pages:
 * next pages are fetched until today's publication is found or a later one is loaded.
 */
export const useTodayPublication = ({
  data,
  hasNextPage,
  isFetchingNextPage,
  isError,
  isPending,
  fetchNextPage,
}: ReturnType<typeof usePublicationsOfCommunityQuery>) => {
  const todayPublication = data?.find((p) => dayjs(p.effectivePublicationDate).isSame(dayjs(), 'day'))
  const lastLoadedPublication = data?.at(-1)
  const shouldLoadMore =
    !todayPublication &&
    !!lastLoadedPublication &&
    dayjs(lastLoadedPublication.effectivePublicationDate).isBefore(dayjs(), 'day') &&
    hasNextPage &&
    !isFetchingNextPage &&
    // Without this, a failed fetch would make shouldLoadMore toggle back to true and refetch endlessly
    !isError

  useEffect(() => {
    if (shouldLoadMore) fetchNextPage()
  }, [shouldLoadMore, fetchNextPage])

  return {
    todayPublication,
    isSearching: !todayPublication && (isPending || shouldLoadMore || isFetchingNextPage),
  }
}
